import moment from "moment";
import { Fragment, useMemo, useState } from "react";

import { formatPrice } from "utils/formats";

import { ProfessionalAggregate } from "../../aggregate";
import * as S from "../../styles";

interface RedeProfessionalsTableProps {
  rows: ProfessionalAggregate[];
  isLoading: boolean;
}

const pct = (v: number) => `${(v * 100).toFixed(1)}%`;

// Iniciais sem títulos (Dr./Dra.) para o avatar.
const initials = (name: string) => {
  const parts = name
    .replace(/\b(dr|dra|sr|sra)\.?\b/gi, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return (parts.slice(0, 2).map((w) => w[0]).join("") || "?").toUpperCase();
};

export default function RedeProfessionalsTable({
  rows,
  isLoading,
}: RedeProfessionalsTableProps) {
  const [openId, setOpenId] = useState<number | null>(null);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, p) => ({
          bruto: acc.bruto + p.bruto,
          repasse: acc.repasse + p.repasse,
          retido: acc.retido + p.retido,
          count: acc.count + p.count,
        }),
        { bruto: 0, repasse: 0, retido: 0, count: 0 }
      ),
    [rows]
  );

  const toggle = (id: number) => setOpenId((cur) => (cur === id ? null : id));

  return (
    <section className="card p-0 overflow-hidden" aria-label="Profissionais que geraram orçamentos">
      <header className="card-header border-bottom d-flex flex-wrap align-items-center justify-content-between gap-2 py-3 px-3">
        <span className="fw-bold d-inline-flex align-items-center gap-2">
          <i className="uil uil-user-md text-primary fs-5" aria-hidden="true"></i>
          Profissionais que geraram orçamentos
        </span>
        {!isLoading && rows.length > 0 && (
          <div className="d-flex flex-wrap gap-3 small">
            <span className="text-muted">
              Transacionado{" "}
              <strong className="text-success">{formatPrice(totals.bruto)}</strong>
            </span>
            <span className="text-muted">
              Repassado{" "}
              <strong className="text-primary">{formatPrice(totals.repasse)}</strong>
            </span>
            <span className="text-muted">
              Retido{" "}
              <strong className="text-secondary">{formatPrice(totals.retido)}</strong>
            </span>
          </div>
        )}
      </header>

      {isLoading ? (
        <div className="p-3 d-flex flex-column gap-3" aria-label="Carregando profissionais">
          {[...Array(5)].map((_, i) => (
            <S.Skeleton key={i} height="1.5rem" />
          ))}
        </div>
      ) : rows.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover mb-0 small align-middle">
            <thead className="table-light">
              <tr>
                <th className="ps-3">Profissional</th>
                <th className="text-center d-none d-sm-table-cell">Orçamentos</th>
                <th className="text-end d-none d-lg-table-cell">Transacionado</th>
                <th className="text-end">Repassado</th>
                <th className="text-end d-none d-lg-table-cell pe-3">Retido</th>
                <th className="text-end pe-3" style={{ width: 48 }} aria-label="Detalhe"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => {
                const isOpen = openId === p.id;
                const detailId = `split-detail-${p.id}`;
                return (
                  <Fragment key={p.id}>
                    <S.ClickableRow
                      className={isOpen ? "table-active" : undefined}
                      onClick={() => toggle(p.id)}
                    >
                      <td className="ps-3">
                        <div className="d-flex align-items-center gap-2" style={{ maxWidth: 260 }}>
                          <S.Avatar aria-hidden="true">{initials(p.name)}</S.Avatar>
                          <span className="text-truncate" title={p.name}>
                            {p.name}
                          </span>
                        </div>
                      </td>
                      <td className="text-center d-none d-sm-table-cell">
                        <span className="badge rounded-pill bg-light text-dark border">
                          {p.count}
                        </span>
                      </td>
                      <td className="text-end d-none d-lg-table-cell">
                        {formatPrice(p.bruto)}
                      </td>
                      <td className="text-end fw-bold text-primary">
                        {formatPrice(p.repasse)}
                      </td>
                      <td className="text-end d-none d-lg-table-cell pe-3 text-secondary">
                        {formatPrice(p.retido)}
                      </td>
                      <td className="text-end pe-3">
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          aria-expanded={isOpen}
                          aria-controls={detailId}
                          aria-label={
                            isOpen
                              ? `Ocultar detalhe do split de ${p.name}`
                              : `Ver detalhe do split de ${p.name}`
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            toggle(p.id);
                          }}
                        >
                          <i
                            className={`uil ${isOpen ? "uil-angle-up" : "uil-angle-down"} fs-4`}
                            aria-hidden="true"
                          ></i>
                        </button>
                      </td>
                    </S.ClickableRow>

                    {isOpen && (
                      <tr id={detailId}>
                        <td colSpan={6} className="bg-light px-3 py-3">
                          {p.hasData ? (
                            <>
                              <div className="fw-bold mb-3 d-flex align-items-center gap-2">
                                <i className="uil uil-balance-scale text-primary" aria-hidden="true"></i>
                                Detalhe do split — {p.name}
                              </div>

                              {/* KPIs em R$ */}
                              <div className="row g-2 mb-3">
                                <div className="col-12 col-md-4">
                                  <div className="border rounded-3 p-3 h-100">
                                    <div className="small text-muted mb-1">Transacionado</div>
                                    <div className="fs-5 fw-bold text-success">
                                      {formatPrice(p.bruto)}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="border rounded-3 p-3 h-100">
                                    <div className="small text-muted mb-1">
                                      Repassado ao profissional
                                    </div>
                                    <div className="fs-5 fw-bold text-primary">
                                      {formatPrice(p.repasse)}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-12 col-md-4">
                                  <div className="border rounded-3 p-3 h-100">
                                    <div className="small text-muted mb-1">
                                      Retido pela plataforma
                                    </div>
                                    <div className="fs-5 fw-bold text-secondary">
                                      {formatPrice(p.retido)}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Barra de split médico x plataforma */}
                              <div
                                className="progress mb-1"
                                style={{ height: 10 }}
                                role="img"
                                aria-label={`Split: ${pct(
                                  p.effectiveSplitDoctorPct
                                )} ao profissional, ${pct(
                                  p.effectiveSplitPlatformPct
                                )} retido pela plataforma`}
                              >
                                <div
                                  className="progress-bar bg-primary"
                                  style={{ width: pct(p.effectiveSplitDoctorPct) }}
                                />
                                <div
                                  className="progress-bar bg-secondary"
                                  style={{ width: pct(p.effectiveSplitPlatformPct) }}
                                />
                              </div>
                              <div className="d-flex justify-content-between small text-muted mb-3">
                                <span>
                                  <i className="uil uil-circle text-primary" aria-hidden="true"></i>{" "}
                                  Profissional {pct(p.effectiveSplitDoctorPct)}
                                </span>
                                <span>
                                  Plataforma {pct(p.effectiveSplitPlatformPct)}{" "}
                                  <i className="uil uil-circle text-secondary" aria-hidden="true"></i>
                                </span>
                              </div>

                              {/* Detalhamento por orçamento */}
                              <div className="table-responsive">
                                <table className="table table-sm mb-0 small align-middle">
                                  <thead className="table-light">
                                    <tr>
                                      <th className="ps-2">Data</th>
                                      <th>Paciente</th>
                                      <th className="text-end">Transacionado</th>
                                      <th className="text-end">Repassado</th>
                                      <th className="text-end pe-2">Retido</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {p.transactions.map((t) => (
                                      <tr key={t.id}>
                                        <td className="ps-2">
                                          {t.date ? moment(t.date).format("DD/MM/YYYY") : "—"}
                                        </td>
                                        <td style={{ maxWidth: 180 }}>
                                          <span
                                            className="d-block text-truncate"
                                            title={t.patientName}
                                          >
                                            {t.patientName}
                                          </span>
                                        </td>
                                        <td className="text-end">
                                          {formatPrice(t.transacionado)}
                                        </td>
                                        <td className="text-end fw-semibold text-primary">
                                          {formatPrice(t.repassado)}
                                        </td>
                                        <td className="text-end pe-2 text-secondary">
                                          {formatPrice(t.retido)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr className="fw-bold border-top">
                                      <td className="ps-2" colSpan={2}>
                                        Total ({p.count})
                                      </td>
                                      <td className="text-end text-success">
                                        {formatPrice(p.bruto)}
                                      </td>
                                      <td className="text-end text-primary">
                                        {formatPrice(p.repasse)}
                                      </td>
                                      <td className="text-end pe-2 text-secondary">
                                        {formatPrice(p.retido)}
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>

                              <small className="text-muted d-block mt-2">
                                Valores repassados calculados a partir das transações
                                concluídas/agendadas do período (repasse em D+2 úteis).
                              </small>
                            </>
                          ) : (
                            <small className="text-muted">
                              Sem transações no período para detalhar o split.
                            </small>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-muted text-center py-5">
          <i className="uil uil-user-md display-4 d-block mb-2 opacity-25" aria-hidden="true"></i>
          <small className="d-block">Nenhum profissional gerou orçamentos no período.</small>
          <small>Tente ampliar o período ou remover os filtros.</small>
        </div>
      )}
    </section>
  );
}
